import { useCallback, useEffect, useMemo, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Link, useNavigate } from "react-router";
import { ChevronLeft, RefreshCw } from "lucide-react";
import { ZoneCard } from "./defaults/ZoneCard";
import { DocumentDesignEditor } from "./defaults/DocumentDesignEditor";
import { HeaderZone } from "./defaults/HeaderZone";
import { FooterZone } from "./defaults/FooterZone";
import { MarginsZone, type MarginValues } from "./defaults/MarginsZone";
import { CountersignerZone } from "./defaults/CountersignerZone";
import { SignatureBlockZone } from "./defaults/SignatureBlockZone";
import { PreviewPane } from "./defaults/PreviewPane";
import { useDirtyTracking } from "./defaults/useDirtyTracking";
import { usePreviewIntegration } from "./defaults/usePreviewIntegration";
import { useToast } from "../../components/ui/Toast";
import { Tooltip } from "../../components/ui";

const TENANT_DEFAULTS = gql`
  query TenantSettingsDefaults {
    tenantSettings {
      id
      name
      defaultHeaderHtml
      defaultFooterHtml
      defaultCountersignerName
      defaultCountersignerSignatureKey
      defaultMarginTop
      defaultMarginBottom
      defaultMarginLeft
      defaultMarginRight
      defaultTopLeftHtml
      defaultTopRightHtml
      defaultShowFirstPageMargins
      defaultSignatureBlockHtml
      defaultPreviewImageUrl
      defaultPreviewContentHash
      defaultPreviewGeneratedAt
      cdnBaseUrl
    }
  }
`;

const UPDATE_TENANT_SETTINGS = gql`
  mutation UpdateTenantSettings(
    $input: TenantSettingsInput!
    $previewContentHash: String
  ) {
    updateTenantSettings(input: $input, previewContentHash: $previewContentHash) {
      id
      defaultHeaderHtml
      defaultFooterHtml
      defaultCountersignerName
      defaultCountersignerSignatureKey
      defaultMarginTop
      defaultMarginBottom
      defaultMarginLeft
      defaultMarginRight
      defaultTopLeftHtml
      defaultTopRightHtml
      defaultShowFirstPageMargins
      defaultSignatureBlockHtml
      defaultPreviewImageUrl
      defaultPreviewContentHash
      defaultPreviewGeneratedAt
    }
  }
`;

interface TenantSettings {
  id: string;
  name: string;
  defaultHeaderHtml: string | null;
  defaultFooterHtml: string | null;
  defaultCountersignerName: string | null;
  defaultCountersignerSignatureKey: string | null;
  defaultMarginTop: number | null;
  defaultMarginBottom: number | null;
  defaultMarginLeft: number | null;
  defaultMarginRight: number | null;
  defaultTopLeftHtml: string | null;
  defaultTopRightHtml: string | null;
  defaultShowFirstPageMargins: boolean | null;
  defaultSignatureBlockHtml: string | null;
  defaultPreviewImageUrl: string | null;
  defaultPreviewContentHash: string | null;
  defaultPreviewGeneratedAt: string | null;
  cdnBaseUrl: string;
}

interface FormValues {
  headerHtml: string;
  topLeftHtml: string;
  topRightHtml: string;
  footerHtml: string; // bottom-left margin (column kept for legacy)
  countersignerName: string;
  signatureBlockHtml: string;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  countersignerSignatureKey: string;
  showFirstPageMargins: boolean;
}

const DEFAULT_VALUES: FormValues = {
  headerHtml: "",
  topLeftHtml: "",
  topRightHtml: "",
  footerHtml: "",
  countersignerName: "",
  signatureBlockHtml: "",
  marginTop: 0.75,
  marginBottom: 0.75,
  marginLeft: 0.5,
  marginRight: 0.5,
  countersignerSignatureKey: "",
  showFirstPageMargins: false,
};

function valuesFromTenant(t: TenantSettings | undefined): FormValues {
  if (!t) return DEFAULT_VALUES;
  return {
    headerHtml: t.defaultHeaderHtml ?? "",
    topLeftHtml: t.defaultTopLeftHtml ?? "",
    topRightHtml: t.defaultTopRightHtml ?? "",
    footerHtml: t.defaultFooterHtml ?? "",
    countersignerName: t.defaultCountersignerName ?? "",
    signatureBlockHtml: t.defaultSignatureBlockHtml ?? "",
    marginTop: t.defaultMarginTop ?? 0.75,
    marginBottom: t.defaultMarginBottom ?? 0.75,
    marginLeft: t.defaultMarginLeft ?? 0.5,
    marginRight: t.defaultMarginRight ?? 0.5,
    countersignerSignatureKey: t.defaultCountersignerSignatureKey ?? "",
    showFirstPageMargins: t.defaultShowFirstPageMargins ?? false,
  };
}

function StatusPill({
  status,
  isStale,
}: {
  status: ReturnType<typeof usePreviewIntegration>["status"];
  isStale: boolean;
}) {
  let label = "No preview yet";
  let cls =
    "bg-canvas hair border border-ink-6 text-ink-4 [&_.dot]:bg-ink-5";
  if (status === "generating") {
    label = "Generating preview…";
    cls = "bg-tide-ss hair border border-tide/40 text-tide [&_.dot]:bg-tide";
  } else if (status === "failed") {
    label = "Preview failed";
    cls = "bg-rust-s/40 hair border border-rust/40 text-rust [&_.dot]:bg-rust";
  } else if (isStale || status === "stale") {
    label = "Preview out of date";
    cls = "bg-spark-s/40 hair border border-spark-s text-spark [&_.dot]:bg-spark";
  } else if (status === "ready") {
    label = "Preview up to date";
    cls = "bg-sage-s/40 hair border border-sage-s text-sage [&_.dot]:bg-sage";
  }
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11.5px] font-medium rounded-full px-2 py-0.5 ${cls}`}
    >
      <span className="dot"></span>
      {label}
    </span>
  );
}

export function DefaultsPane() {
  const navigate = useNavigate();
  const toast = useToast();
  const { data, loading, error, refetch } = useQuery<{
    tenantSettings: TenantSettings;
  }>(TENANT_DEFAULTS, { fetchPolicy: "cache-and-network" });
  const [save, saveResult] = useMutation(UPDATE_TENANT_SETTINGS);

  const tenant = data?.tenantSettings;
  const tracking = useDirtyTracking<FormValues>(DEFAULT_VALUES);

  // Initial-hydrate the form from the loaded query exactly once.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (!hydrated && tenant) {
      tracking.markSaved(valuesFromTenant(tenant));
      setHydrated(true);
    }
    // tracking is stable per-render; only run when tenant arrives
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, hydrated]);

  const buildPreviewPayload = useCallback(() => {
    const v = tracking.values;
    return {
      isDefaultsPreview: true,
      agreementData: {
        headerHtml: v.headerHtml,
        topLeftHtml: v.topLeftHtml,
        topRightHtml: v.topRightHtml,
        footerHtml: v.footerHtml,
        countersignerName: v.countersignerName,
        countersignerSignatureKey: v.countersignerSignatureKey,
        marginTop: v.marginTop,
        marginBottom: v.marginBottom,
        marginLeft: v.marginLeft,
        marginRight: v.marginRight,
        signatureBlockHtml: v.signatureBlockHtml,
        showFirstPageMargins: v.showFirstPageMargins,
      },
    };
  }, [tracking.values]);

  const preview = usePreviewIntegration({
    initialHash: tenant?.defaultPreviewContentHash ?? null,
    initialPreviewUrl: tenant?.defaultPreviewImageUrl ?? null,
    initialGeneratedAt: tenant?.defaultPreviewGeneratedAt ?? null,
    idleMs: 20000,
    buildTriggerPayload: buildPreviewPayload,
  });

  // Re-fetch tenant settings after a successful preview render so we
  // pick up the new content hash and image URL the renderer wrote.
  useEffect(() => {
    if (preview.status === "ready" && preview.previewHash == null && hydrated) {
      void refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview.status]);

  // Mark stale whenever the dirty values diverge from the saved snapshot.
  useEffect(() => {
    if (!hydrated) return;
    if (tracking.isDirty) preview.markStale();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracking.values, hydrated]);

  // Idle countdown displayed in the toolbar.
  const [idleRemaining, setIdleRemaining] = useState<number | null>(null);
  useEffect(() => {
    if (!preview.isStale) {
      setIdleRemaining(null);
      return;
    }
    let secondsLeft = 20;
    setIdleRemaining(secondsLeft);
    const id = window.setInterval(() => {
      secondsLeft -= 1;
      if (secondsLeft <= 0) {
        setIdleRemaining(null);
        window.clearInterval(id);
      } else {
        setIdleRemaining(secondsLeft);
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [preview.isStale, tracking.values]);

  const previewHash =
    preview.previewHash ?? tenant?.defaultPreviewContentHash ?? null;

  const canSave = useMemo(() => {
    if (saveResult.loading) return false;
    if (!tracking.isDirty) return false;
    // Never-previewed: allow first save.
    if (tenant?.defaultPreviewContentHash == null) return true;
    if (preview.status !== "ready") return false;
    if (preview.isStale) return false;
    return true;
  }, [
    saveResult.loading,
    tracking.isDirty,
    tenant?.defaultPreviewContentHash,
    preview.status,
    preview.isStale,
  ]);

  // Why is the save button disabled? Surfaced via tooltip on the
  // disabled buttons. We only narrate the preview-gating cases, since
  // "no changes yet" is self-evident from a clean form.
  const saveDisabledReason = useMemo<string | null>(() => {
    if (canSave) return null;
    if (saveResult.loading) return null;
    if (!tracking.isDirty) return null;
    if (tenant?.defaultPreviewContentHash == null) return null;
    if (preview.status === "generating")
      return "Generating preview… save unlocks when it's ready.";
    if (preview.isStale || preview.status === "stale")
      return "Refresh the preview before saving.";
    if (preview.status !== "ready")
      return "Refresh the preview before saving.";
    return null;
  }, [
    canSave,
    saveResult.loading,
    tracking.isDirty,
    tenant?.defaultPreviewContentHash,
    preview.status,
    preview.isStale,
  ]);

  async function handleSave(thenGoBack: boolean) {
    const v = tracking.values;
    try {
      const result = await save({
        variables: {
          input: {
            // Send empty strings as-is. The resolver's `is not None` gate
            // treats null as "skip this field," so an empty save would
            // silently no-op and the form would re-hydrate with the old
            // value -- looking like a revert. Pass-through lets the empty
            // string land in the column (which is nullable; "" is fine).
            defaultHeaderHtml: v.headerHtml,
            defaultTopLeftHtml: v.topLeftHtml,
            defaultTopRightHtml: v.topRightHtml,
            defaultFooterHtml: v.footerHtml,
            defaultCountersignerName: v.countersignerName,
            defaultCountersignerSignatureKey: v.countersignerSignatureKey,
            defaultMarginTop: v.marginTop,
            defaultMarginBottom: v.marginBottom,
            defaultMarginLeft: v.marginLeft,
            defaultMarginRight: v.marginRight,
            defaultShowFirstPageMargins: v.showFirstPageMargins,
            defaultSignatureBlockHtml: v.signatureBlockHtml,
          },
          previewContentHash: previewHash,
        },
      });
      const updated = result.data?.updateTenantSettings;
      if (updated) {
        tracking.markSaved(valuesFromTenant({ ...tenant!, ...updated }));
      }
      toast.push({
        kind: "success",
        title: "Agreement design saved",
        description: thenGoBack
          ? "Returning to your agreements list."
          : "Defaults applied to every agreement.",
      });
      if (thenGoBack) navigate("/settings/agreements");
    } catch (e) {
      const message = e instanceof Error ? e.message : "Unknown error";
      const isStale = message.includes("Preview is out of date");
      toast.push({
        kind: "error",
        title: isStale
          ? "Preview is out of date"
          : "Couldn't save agreement design",
        description: isStale
          ? "Refresh the preview, then try Save again."
          : message,
      });
    }
  }

  if (loading && !tenant) {
    return (
      <div className="px-7 py-10 text-ink-4 text-[13px]">
        Loading defaults…
      </div>
    );
  }
  if (error || !tenant) {
    return (
      <div className="px-7 py-10 text-rust text-[13px]">
        Couldn't load tenant defaults: {error?.message ?? "missing tenant"}
      </div>
    );
  }

  const v = tracking.values;
  const setField = tracking.setField;

  return (
    <div className="min-h-full">
      {/* Sticky header bar (replaces SettingsShell breadcrumb for this route) */}
      <div className="hair-b border-b sticky top-[var(--banner-h)] z-20 bg-paper/90 backdrop-blur">
        <div className="h-14 px-7 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link
              to="/settings/agreements"
              className="text-ink-4 hover:text-ink-2 flex items-center gap-1.5 text-[12.5px]"
            >
              <ChevronLeft size={14} strokeWidth={2} />
              Agreements
            </Link>
            <span className="text-ink-5">›</span>
            <span className="text-ink font-medium text-[13.5px]">Defaults</span>
            <StatusPill status={preview.status} isStale={preview.isStale} />
          </div>
          <div className="flex items-center gap-2">
            {tracking.dirtyCount > 0 && (
              <span className="text-[12px] text-ink-4 mr-2">
                {tracking.dirtyCount} unsaved field
                {tracking.dirtyCount === 1 ? "" : "s"}
              </span>
            )}
            <button
              type="button"
              onClick={tracking.reset}
              disabled={!tracking.isDirty || saveResult.loading}
              className="px-3 py-1.5 rounded-md hair border bg-paper-hi text-[12.5px] text-ink-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={preview.triggerPreview}
              disabled={preview.status === "generating"}
              className="px-3 py-1.5 rounded-md hair border bg-paper-hi text-[12.5px] text-ink-3 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw size={12} strokeWidth={2} />
              Refresh preview
            </button>
            <Tooltip label={saveDisabledReason} side="bottom">
              <button
                type="button"
                onClick={() => handleSave(false)}
                disabled={!canSave}
                className="px-3 py-1.5 rounded-md hair border bg-paper-hi text-[12.5px] text-ink-3 disabled:bg-paper-2 disabled:text-ink-5 disabled:border-ink-6 disabled:cursor-not-allowed"
              >
                Save &amp; keep editing
              </button>
            </Tooltip>
            <Tooltip label={saveDisabledReason} side="bottom">
              <button
                type="button"
                onClick={() => handleSave(true)}
                disabled={!canSave}
                className="px-3.5 py-1.5 rounded-md bg-tide text-paper-hi text-[12.5px] font-medium hover:bg-tide-d disabled:bg-ink-6 disabled:cursor-not-allowed"
              >
                Save &amp; go back
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 min-h-[calc(100vh-3.5rem)]">
        {/* Editor rail */}
        <div className="col-span-12 lg:col-span-5 bg-paper">
          <div className="px-7 py-7">
            <div className="text-[11.5px] text-tide font-medium uppercase tracking-wider mb-1.5">
              Defaults
            </div>
            <h2 className="font-display text-[28px] font-medium tracking-tight">
              Agreement design
            </h2>
            <p className="text-[12.5px] text-ink-3 mt-1.5 max-w-md">
              These settings apply to every agreement unless overridden.
              Edit a zone, then refresh the preview to enable Save.
            </p>
          </div>

          <div className="px-7 space-y-2 pb-12">
            <ZoneCard
              zoneNumber={1}
              title="Header"
              hint="Page 1 only. Pages 2+ render a simplified running header (logo + booking ID)."
            >
              <HeaderZone
                value={v.headerHtml}
                onChange={(next) => setField("headerHtml", next)}
              />
            </ZoneCard>

            <ZoneCard
              zoneNumber={2}
              title="Top-left margin"
              hint="Runs in the top-left corner from page 2 onward. Use {{page_number}} / {{total_pages}} for page numbering."
            >
              <DocumentDesignEditor
                value={v.topLeftHtml}
                onChange={(next) => setField("topLeftHtml", next)}
                height={220}
                uploadType="top_left"
              />
            </ZoneCard>

            <ZoneCard
              zoneNumber={3}
              title="Top-right margin"
              hint="Runs in the top-right corner from page 2 onward."
            >
              <DocumentDesignEditor
                value={v.topRightHtml}
                onChange={(next) => setField("topRightHtml", next)}
                height={220}
                uploadType="top_right"
              />
            </ZoneCard>

            <ZoneCard
              zoneNumber={4}
              title="Bottom-left margin"
              hint="Runs in the bottom-left corner of every page. Bottom-right is always the booking ID."
            >
              <FooterZone
                value={v.footerHtml}
                onChange={(next) => setField("footerHtml", next)}
              />
            </ZoneCard>

            <ZoneCard zoneNumber={5} title="Margins">
              <MarginsZone
                value={{
                  top: v.marginTop,
                  bottom: v.marginBottom,
                  left: v.marginLeft,
                  right: v.marginRight,
                }}
                onChange={(next: MarginValues) => {
                  tracking.setValues({
                    ...v,
                    marginTop: next.top,
                    marginBottom: next.bottom,
                    marginLeft: next.left,
                    marginRight: next.right,
                  });
                }}
                showFirstPageMargins={v.showFirstPageMargins}
                onChangeShowFirstPageMargins={(next) =>
                  setField("showFirstPageMargins", next)
                }
              />
            </ZoneCard>

            <ZoneCard zoneNumber={6} title="Countersigner">
              <CountersignerZone
                name={v.countersignerName}
                signatureKey={v.countersignerSignatureKey || null}
                cdnBaseUrl={tenant.cdnBaseUrl}
                onChangeName={(next) => setField("countersignerName", next)}
                onChangeSignatureKey={(next) =>
                  setField("countersignerSignatureKey", next ?? "")
                }
              />
            </ZoneCard>

            <ZoneCard
              zoneNumber={7}
              title="Signature block"
              hint="Legal-effect statement that appears above the customer's signature."
            >
              <SignatureBlockZone
                value={v.signatureBlockHtml}
                onChange={(next) => setField("signatureBlockHtml", next)}
              />
            </ZoneCard>
          </div>
        </div>

        {/* Live preview pane — pinned so the editor rail can scroll past */}
        <div
          className="hidden lg:flex lg:col-span-7 flex-col sticky self-start"
          style={{
            top: "calc(var(--banner-h) + 3.5rem)",
            height: "calc(100vh - var(--banner-h) - 3.5rem)",
          }}
        >
          <PreviewPane
            status={preview.status}
            isStale={preview.isStale}
            previewUrl={preview.previewUrl}
            generatedAt={preview.generatedAt}
            idleSecondsRemaining={idleRemaining}
            error={preview.error}
            pageInfo={`Defaults applied to ${tenant.name}`}
          />
        </div>
      </div>
    </div>
  );
}
